import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, ArrowLeft, Search, User } from 'lucide-react';
import { toast } from 'sonner';

const ChatPage = () => {
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [searchUsers, setSearchUsers] = useState('');
  const [foundUsers, setFoundUsers] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.get(`${API}/conversations`, { headers, withCredentials: true });
      setConversations(res.data.conversations || []);
    } catch {}
  }, [token]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  useEffect(() => {
    if (!activeConvo) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const fetchMessages = async () => {
      try {
        const res = await axios.get(`${API}/messages/${activeConvo.conversation_id}`, { headers, withCredentials: true });
        setMessages(res.data.messages || []);
      } catch {}
    };
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [activeConvo, token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || !activeConvo) return;
    setSending(true);
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: activeConvo.other_user_id,
        content: newMsg.trim(),
      }, { headers, withCredentials: true });
      setNewMsg('');
      const res = await axios.get(`${API}/messages/${activeConvo.conversation_id}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setMessages(res.data.messages || []);
      fetchConversations();
    } catch { toast.error('Failed to send'); }
    setSending(false);
  };

  const searchForUsers = async (q) => {
    setSearchUsers(q);
    if (q.length < 2) { setFoundUsers([]); return; }
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.get(`${API}/chat/users/search?search=${q}&limit=10`, { headers, withCredentials: true });
      setFoundUsers(res.data.users || []);
    } catch {
      setFoundUsers([]);
    }
  };

  const startChat = (targetUser) => {
    const participants = [user.user_id, targetUser.user_id].sort();
    const convoId = `conv_${participants.join('_')}`;
    setActiveConvo({
      conversation_id: convoId,
      other_user_id: targetUser.user_id,
      other_user_name: targetUser.name,
    });
    setShowNewChat(false);
    setSearchUsers('');
    setFoundUsers([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-140px)] flex" data-testid="chat-page">
        {/* Sidebar */}
        <div className={`w-full md:w-80 border-r bg-white flex flex-col ${activeConvo ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Messages</h2>
              <Button variant="outline" size="sm" onClick={() => setShowNewChat(!showNewChat)} data-testid="new-chat-btn">
                <MessageSquare className="w-4 h-4" />
              </Button>
            </div>
            {showNewChat && (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search users..." value={searchUsers} onChange={(e) => searchForUsers(e.target.value)} className="pl-9" data-testid="user-search" />
                </div>
                {foundUsers.length > 0 && (
                  <div className="max-h-40 overflow-y-auto border rounded-lg">
                    {foundUsers.map(u => (
                      <button key={u.user_id} onClick={() => startChat(u)} className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors text-sm" data-testid={`start-chat-${u.user_id}`}>
                        <p className="font-medium text-slate-900">{u.name}</p>
                        <p className="text-xs text-slate-500">{u.role}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length > 0 ? conversations.map(c => (
              <button
                key={c.conversation_id}
                onClick={() => setActiveConvo(c)}
                data-testid={`convo-${c.conversation_id}`}
                className={`w-full text-left px-4 py-3 border-b hover:bg-slate-50 transition-colors ${activeConvo?.conversation_id === c.conversation_id ? 'bg-indigo-50' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-medium text-slate-900 text-sm truncate">{c.other_user_name}</p>
                  {c.unread_count > 0 && <Badge className="bg-indigo-600 text-white text-xs">{c.unread_count}</Badge>}
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5">{c.last_message}</p>
              </button>
            )) : (
              <div className="text-center py-12 text-slate-400 text-sm">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No conversations yet
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col bg-slate-50 ${!activeConvo ? 'hidden md:flex' : 'flex'}`}>
          {activeConvo ? (
            <>
              <div className="px-4 py-3 bg-white border-b flex items-center gap-3">
                <Button variant="ghost" size="sm" className="md:hidden" onClick={() => setActiveConvo(null)}>
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-indigo-600" />
                </div>
                <p className="font-medium text-slate-900" data-testid="chat-recipient-name">{activeConvo.other_user_name}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map(m => (
                  <div key={m.message_id} className={`flex ${m.sender_id === user.user_id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${
                      m.sender_id === user.user_id
                        ? 'bg-indigo-600 text-white rounded-br-md'
                        : 'bg-white text-slate-900 border rounded-bl-md'
                    }`} data-testid={`msg-${m.message_id}`}>
                      <p>{m.content}</p>
                      <p className={`text-xs mt-1 ${m.sender_id === user.user_id ? 'text-indigo-200' : 'text-slate-400'}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div className="p-4 bg-white border-t">
                <div className="flex gap-2">
                  <Input
                    value={newMsg}
                    onChange={(e) => setNewMsg(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button onClick={handleSend} disabled={sending || !newMsg.trim()} data-testid="send-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Select a conversation or start a new chat</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ChatPage;
